/**
 * ReadinessHero — V98-5 review T5-H1: ONE readiness card for FedRAMP KSIs
 * (Prepare and Operate): the big %, the split as a labelled meter (met /
 * partial / gaps, gaps hatched so it never depends on colour alone), "N to go
 * · ~N a week to be ready by <date>" (no history yet, so pace only — a delta
 * needs snapshots), a days-left chip, and one filled primary.
 */
import type { ReactNode } from 'react';
import { readinessPace } from '../../lib/program-shell';
import { CouldntCheckCount } from '../CouldntCheck';
import { Chip } from '../today/shared';
import '../../pages/programs.css';
import '../../pages/cert-ops.css';

export function ReadinessHero({ id, label, pct, split, noun, by, daysLeft, action, children, onCouldnt }: {
  id: string; label: string; pct: number;
  /** V109-1-19 (B4): `couldnt` — its own segment, never inside partial or gaps (met + partial + gaps + couldnt = total). */
  split: { met: number; partial: number; gaps: number; couldnt?: number; total: number };
  noun: [string, string]; by: string | null; daysLeft: number | null; action: ReactNode; children?: ReactNode;
  /** Shows the ones GAC Pro couldn't check (filters to them and lands on the first fix). */
  onCouldnt?: () => void;
}) {
  const cc = split.couldnt ?? 0;
  const pace = readinessPace(split.gaps + split.partial + cc, daysLeft, noun, by);
  return (
    <section className="gp-hero" aria-labelledby={id} data-part="readiness-hero">
      <h3 id={id} className="gp-hero-label">{label}</h3>
      <p className="gp-hero-figure"><b>{`${Math.round(pct)}%`}</b><span className="gp-dim">{` ready · ${split.met} of ${split.total} ${noun[1]} met`}</span>
        {daysLeft !== null && daysLeft >= 0 ? <> <Chip text={`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`} tone={daysLeft <= 14 ? 'amber' : 'neutral'} /></> : null}</p>
      <div className="gp-ksi-meter" role="img" data-part="ksi-meter" aria-label={`${split.met} met, ${split.partial} partial, ${split.gaps} gaps${cc ? `, ${cc} couldn't be checked` : ''}, of ${split.total}`}>
        {split.met ? <span className="gp-ksi-seg gp-ksi-seg--met" style={{ flexGrow: split.met }} /> : null}
        {split.partial ? <span className="gp-ksi-seg gp-ksi-seg--partial" style={{ flexGrow: split.partial }} /> : null}
        {cc ? <span className="gp-ksi-seg gp-ksi-seg--couldnt" style={{ flexGrow: cc }} /> : null}
        {split.gaps ? <span className="gp-ksi-seg gp-ksi-seg--gap" style={{ flexGrow: split.gaps }} /> : null}
      </div>
      <p className="gp-meter-legend" aria-hidden="true">
        <span><i className="gp-ksi-seg--met" />{`${split.met} met`}</span>
        <span><i className="gp-ksi-seg--partial" />{`${split.partial} partial`}</span>
        {cc ? <span><i className="gp-ksi-seg--couldnt" />{`${cc} couldn't check`}</span> : null}
        <span><i className="gp-ksi-seg--gap" />{`${split.gaps} gap${split.gaps === 1 ? '' : 's'}`}</span>
      </p>
      {/* V109-1-19 R3 (Medium): the one hero count component (SOC 2, FedRAMP, GovRAMP), the glyph inline. */}
      {onCouldnt ? <CouldntCheckCount count={cc} noun={noun} onClick={onCouldnt} /> : null}
      {pace ? <p className="gp-hero-pace" data-part="pace"><b>{pace.delta}</b>{pace.pace ? <span className="gp-dim">{` · ${pace.pace}`}</span> : null}</p> : null}
      {children}
      <div className="gp-actions">{action}</div>
    </section>
  );
}
